import { SessionDataParser } from "../sessionDataProvider/sessionDataParser.js";
import Regions from "../content/regions.js";
import { removeCookie, getDocumentCookie } from "./utils.js";

export const cookieNamesList = [
  "last-sign-in-session",
  "awsc-color-theme",
  "noflush_locale",
  "awsc-rac",
  "noflush_awsccs_sid",
];

const { regions } = Regions as {
  regions: { [key: string]: { websiteDomain: string; websiteDomainDualstack: string } };
};

export const wildCardCookies = ["aws-signer-token_"];

export const singleCookies = ["awsd2c-token-c"];

interface Cookie {
  name: string;
  value: string;
}

function getCookies(cookieName: string): Cookie[] {
  return getDocumentCookie()
    .split(";")
    .map((cookieStr) => {
      const [name, value] = cookieStr.trim().split("=");
      return {
        name,
        value,
      };
    })
    .filter(({ name }) => name === cookieName);
}

// Gets cookies matching it's prefix eg : "aws-signer-token_us-east-2"
function getWildCardCookies(cookieName: string): Cookie[] {
  return getDocumentCookie()
    .split(";")
    .map((cookieStr) => {
      const [name, value] = cookieStr.trim().split("=");
      return {
        name,
        value,
      };
    })
    .filter(({ name }) => name.startsWith(cookieName));
}

function removeCookies(cookies: { name: string; domain: string }[]) {
  for (const { name, domain } of cookies) {
    removeCookie(name, domain);
  }
}

function getWebsiteDomains(): string[] {
  const sessionData = SessionDataParser.getInstance().getSessionData();
  let { infrastructureRegion } = sessionData;
  const { dualstackEnabledDomain } = sessionData;

  if (!infrastructureRegion) {
    infrastructureRegion = "us-east-1";
  }

  const result = [];
  const { websiteDomain, websiteDomainDualstack } = regions[infrastructureRegion];
  if (dualstackEnabledDomain) {
    result.push(websiteDomainDualstack);
    result.push(`console.${websiteDomainDualstack}`);
  } else {
    result.push(websiteDomain);
    result.push(`console.${websiteDomain}`);
    if (websiteDomain.split(".").length > 2) {
      // in case it's aws.amazon.com, we would like to clean up
      // on amazon.com
      result.push(websiteDomain.split(".").slice(1).join("."));
    }
  }

  return result.map((domain) => `.${domain}`);
}

function deleteCookies(cookieName: string): void {
  const { hostname } = window.location;

  const isTestTraffic = getCookies("metrics-req-cat").length > 0;
  const cookieDomains = isTestTraffic ? [hostname] : getWebsiteDomains();
  removeCookies(cookieDomains.map((domain) => ({ name: cookieName, domain })));

  const subDomains = hostname.split(".");
  // An heuristic to detect that it's a Prism domain,
  // and also clean a regional cookie that could have been
  // potentially set on <region>.console.aws.amazon.com
  if (subDomains.length > 5) {
    removeCookie(cookieName, subDomains.slice(1).join("."));
  }
}

export function cleanUpPrismCookies() {
  for (const cookieName of cookieNamesList) {
    try {
      const cookies = getCookies(cookieName);
      if (cookies.length > 1) {
        deleteCookies(cookieName);
      }
    } catch (e) {
      console.warn("Couldn't normalize cookies: ", e);
      continue;
    }
  }
}

export function cleanUpPrismWildCardCookies() {
  for (const cookieName of wildCardCookies) {
    try {
      const cookies = getWildCardCookies(cookieName);
      cookies.map((cookie) => {
        deleteCookies(cookie.name);
      });
    } catch (e) {
      console.warn("Couldn't normalize wildCard cookies: ", e);
      continue;
    }
  }
}

export function cleanUpPrismSingleCookies() {
  for (const cookieName of singleCookies) {
    try {
      const cookies = getCookies(cookieName);
      if (cookies.length === 1) {
        removeCookies(getWebsiteDomains().map((domain) => ({ name: cookieName, domain })));
      }
    } catch (e) {
      console.warn("Couldn't normalize single cookies: ", e);
      continue;
    }
  }
}
